import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import CircularProgress from "@material-ui/core/CircularProgress";
import Collapse from "@material-ui/core/Collapse";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import RefreshIcon from "@material-ui/icons/Refresh";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../ConfirmationModal";

const WASHEETS_EXPANDED_KEY = "contactDrawer:washeetsExpanded";
const NO_WASHEET_DOCUMENT = "__no_washeet_document__";

const useStyles = makeStyles(theme => ({
	panel: {
		marginTop: 8,
		padding: 8,
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(1),
	},
	header: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
	},
	row: {
		display: "flex",
		gap: theme.spacing(1),
		alignItems: "center",
	},
	documentRow: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: theme.spacing(1),
		padding: theme.spacing(1),
		border: "1px solid rgba(0, 0, 0, 0.12)",
		marginBottom: theme.spacing(1),
	},
	documentText: {
		minWidth: 0,
		flex: 1,
	},
	documentActions: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(0.5),
	},
	documentId: {
		display: "block",
		color: theme.palette.text.secondary,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},
	status: {
		color: theme.palette.text.secondary,
		fontSize: 12,
	},
	statusSuccess: {
		color: theme.palette.success.main,
	},
	statusError: {
		color: theme.palette.error.main,
	},
	record: {
		padding: theme.spacing(1),
		marginTop: theme.spacing(1),
	},
	recordHeader: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		marginBottom: theme.spacing(1),
	},
	field: {
		marginBottom: theme.spacing(0.5),
		wordBreak: "break-word",
	},
	empty: {
		padding: theme.spacing(1),
		color: theme.palette.text.secondary,
		textAlign: "center",
	},
	progress: {
		marginRight: theme.spacing(1),
	},
	collapseInner: {
		width: "100%",
		display: "flex",
		flexDirection: "column",
	},
	addRecordButton: {
		margin: "10px 0px",
	},
}));

const generateRecordId = () => {
	const chars = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
	return Array.from({ length: 6 }, () =>
		chars.charAt(Math.floor(Math.random() * chars.length))
	).join("");
};

const SYSTEM_HEADERS = ["ID Registro", "Chat ID", "Nombre", "Numero", "Correo"];

const normalizeHeader = value =>
	String(value || "")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, " ")
		.trim();

const orderWasheetHeaders = headers => [
	...SYSTEM_HEADERS,
	...headers.filter(
		header =>
			header &&
			!SYSTEM_HEADERS.some(systemHeader => normalizeHeader(systemHeader) === normalizeHeader(header))
	),
];

const filterByChatId = (rows, chatId) => {
	if (!chatId) return [];
	const normalizedChatId = String(chatId).trim();

	return rows.filter(row => {
		const chatValue = row["Chat ID"];
		return String(chatValue || "").trim() === normalizedChatId;
	});
};

const WasheetsPanel = ({ contact }) => {
	const classes = useStyles();
	const chatId = contact?.chatId || contact?.remoteJid || "";
	const name = contact?.name || "";
	const number = contact?.number || "";
	const email = contact?.email || "";
	const [expanded, setExpanded] = useState(() => {
		const saved = localStorage.getItem(WASHEETS_EXPANDED_KEY);
		return saved === null ? true : saved === "true";
	});
	const [documents, setDocuments] = useState([]);
	const [selectedDocumentId, setSelectedDocumentId] = useState("");
	const [docId, setDocId] = useState("");
	const [sheetName, setSheetName] = useState("");
	const [allowMultipleRecords, setAllowMultipleRecords] = useState(true);
	const [status, setStatus] = useState({
		text: "Selecciona un documento para comenzar.",
		type: "info",
	});
	const [headers, setHeaders] = useState([]);
	const [records, setRecords] = useState([]);
	const [loading, setLoading] = useState(false);
	const [recordDialogOpen, setRecordDialogOpen] = useState(false);
	const [editingRecord, setEditingRecord] = useState(null);
	const [formValues, setFormValues] = useState({});
	const [deletingDocument, setDeletingDocument] = useState(null);

	const selectedDocument = useMemo(
		() =>
			selectedDocumentId === ""
				? null
				: documents.find(document => String(document.id) === String(selectedDocumentId)),
		[documents, selectedDocumentId]
	);

	const updateStatus = (text, type = "info") => {
		setStatus({ text, type });
	};

	const handleToggleExpanded = () => {
		setExpanded(prev => {
			const next = !prev;
			localStorage.setItem(WASHEETS_EXPANDED_KEY, String(next));
			return next;
		});
	};

	const loadDocuments = async () => {
		try {
			const { data } = await api.get("/washeets/documents");
			const nextDocuments = data.documents || [];
			const defaultDocument =
				nextDocuments.find(document => document.isDefault) || nextDocuments[0];

			setDocuments(nextDocuments);
			setSelectedDocumentId(defaultDocument ? String(defaultDocument.id) : "");
		} catch (err) {
			toastError(err);
		}
	};

	const loadSheetData = async (document = selectedDocument) => {
		if (!document) {
			updateStatus("Selecciona un documento para comenzar.", "info");
			setRecords([]);
			return;
		}

		if (!chatId) {
			updateStatus("No hay chat activo seleccionado.", "error");
			setRecords([]);
			return;
		}

		setLoading(true);
		updateStatus("Cargando datos...", "info");

		try {
			const { data } = await api.get("/washeets/read", {
				params: {
					spreadsheetId: document.spreadsheetId,
					sheetName: document.sheetName,
				},
			});

			const nextHeaders = orderWasheetHeaders(data.headers || []);
			const nextRecords = filterByChatId(data.rows || [], chatId);
			setHeaders(nextHeaders);
			setRecords(nextRecords);
			updateStatus(
				`${nextRecords.length} registro(s) encontrados para este chat.`,
				"success"
			);
		} catch (err) {
			updateStatus("No se pudieron cargar los datos del documento.", "error");
			toastError(err);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadDocuments();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (selectedDocument) loadSheetData(selectedDocument);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chatId, selectedDocument?.id]);

	const handleSelectDocument = async value => {
		setSelectedDocumentId(value);
		if (!value) {
			setRecords([]);
			setHeaders([]);
			return;
		}

		try {
			await api.put(`/washeets/documents/${value}/select`);
			setDocuments(prev =>
				prev.map(document => ({
					...document,
					isDefault: String(document.id) === String(value),
				}))
			);
		} catch (err) {
			toastError(err);
		}
	};

	const handleAddDocument = async () => {
		const trimmedDocId = docId.trim();
		const trimmedSheetName = sheetName.trim();

		if (!trimmedDocId) {
			updateStatus("Ingresa el ID del documento.", "error");
			return;
		}

		if (!trimmedSheetName) {
			updateStatus("Ingresa el nombre de la hoja.", "error");
			return;
		}

		if (
			documents.some(
				document =>
					document.spreadsheetId === trimmedDocId &&
					document.sheetName === trimmedSheetName
			)
		) {
			updateStatus("Este documento y hoja ya estan configurados.", "error");
			return;
		}

		try {
			const { data } = await api.post("/washeets/documents", {
				spreadsheetId: trimmedDocId,
				sheetName: trimmedSheetName,
				name: `${trimmedDocId.substring(0, 8)}.../${trimmedSheetName}`,
				allowMultipleRecords,
			});
			await loadDocuments();
			setSelectedDocumentId(String(data.id));
			setDocId("");
			setSheetName("");
			setAllowMultipleRecords(true);
			updateStatus(`Documento "${data.name}" agregado.`, "success");
		} catch (err) {
			toastError(err);
		}
	};

	const handleToggleMultipleRecords = async (document, checked) => {
		try {
			const { data } = await api.put(`/washeets/documents/${document.id}`, {
				allowMultipleRecords: checked,
			});
			setDocuments(prev =>
				prev.map(item =>
					item.id === document.id
						? { ...item, allowMultipleRecords: data.allowMultipleRecords }
						: item
				)
			);
			updateStatus(
				checked
					? "El formulario permite multiples registros."
					: "El formulario usara un registro unico por chat.",
				"success"
			);
		} catch (err) {
			toastError(err);
		}
	};

	const handleDeleteDocument = async documentId => {
		try {
			await api.delete(`/washeets/documents/${documentId}`);
			await loadDocuments();
			setRecords([]);
			setHeaders([]);
			updateStatus("Documento eliminado.", "success");
		} catch (err) {
			toastError(err);
		}
	};

	const openRecordDialog = record => {
		const fields = orderWasheetHeaders(headers.length ? headers : Object.keys(record || {}));
		const values = fields.reduce((acc, field) => {
			if (field === "ID Registro") {
				acc[field] =
					record?.[field] === null || record?.[field] === undefined || record?.[field] === ""
						? generateRecordId()
						: String(record[field]);
			} else if (field === "Chat ID") {
				acc[field] =
					record?.[field] === null || record?.[field] === undefined || record?.[field] === ""
						? chatId
						: String(record[field]);
			} else if (field === "Nombre") {
				acc[field] =
					record?.[field] === null || record?.[field] === undefined || record?.[field] === ""
						? name
						: String(record[field]);
			} else if (field === "Numero") {
				acc[field] =
					record?.[field] === null || record?.[field] === undefined || record?.[field] === ""
						? number
						: String(record[field]);
			} else if (field === "Correo") {
				acc[field] =
					record?.[field] === null || record?.[field] === undefined || record?.[field] === ""
						? email
						: String(record[field]);
			} else if (record) {
				acc[field] =
					record[field] === null || record[field] === undefined
						? ""
						: String(record[field]);
			} else {
				acc[field] = "";
			}
			return acc;
		}, {});

		setEditingRecord(record || null);
		setFormValues(values);
		setRecordDialogOpen(true);
	};

	const handleSaveRecord = async () => {
		if (!selectedDocument) {
			updateStatus("Selecciona un documento primero.", "error");
			return;
		}

		const activeHeaders = orderWasheetHeaders(headers.length ? headers : Object.keys(formValues));
		const payload = editingRecord
			? {
					action: "updateRowByHeaders",
					spreadsheetId: selectedDocument.spreadsheetId,
					sheetName: selectedDocument.sheetName,
					dataByHeader: formValues,
					headers: activeHeaders,
					searchColumn: editingRecord["ID Registro"] ? "ID Registro" : "Chat ID",
					searchValue: editingRecord["ID Registro"] || chatId,
			  }
			: {
					action: "addRow",
					spreadsheetId: selectedDocument.spreadsheetId,
					sheetName: selectedDocument.sheetName,
					headers: activeHeaders,
					values: activeHeaders.map(header => formValues[header] || ""),
			  };

		const wasEditing = !!editingRecord;
		setRecordDialogOpen(false);
		setEditingRecord(null);

		try {
			updateStatus(
				wasEditing ? "Actualizando registro..." : "Guardando registro...",
				"info"
			);
			await api.post("/washeets/write", payload);
			toast.success(wasEditing ? "Registro actualizado." : "Registro guardado.");
			await loadSheetData();
		} catch (err) {
			updateStatus("No se pudo guardar el registro.", "error");
			toastError(err);
		}
	};

	const statusClass =
		status.type === "success"
			? classes.statusSuccess
			: status.type === "error"
			? classes.statusError
			: "";

	return (
		<Paper square variant="outlined" className={classes.panel}>
			<div className={classes.header}>
				<Typography variant="subtitle1">Formularios Google Sheets</Typography>
				<div>
					<IconButton
						size="small"
						disabled={loading || !selectedDocument}
						onClick={() => loadSheetData()}
						title="Actualizar datos"
					>
						<RefreshIcon fontSize="small" />
					</IconButton>
					<IconButton size="small" onClick={handleToggleExpanded}>
						{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
					</IconButton>
				</div>
			</div>

			<Collapse in={expanded} classes={{ wrapperInner: classes.collapseInner }}>
				<Typography variant="subtitle2">Trabajar con documentos</Typography>
				<FormControl variant="outlined" margin="dense" size="small" fullWidth>
					<InputLabel>Documento de Google Sheets</InputLabel>
					<Select
						value={selectedDocumentId || NO_WASHEET_DOCUMENT}
						onChange={event =>
							handleSelectDocument(
								event.target.value === NO_WASHEET_DOCUMENT ? "" : event.target.value
							)
						}
						label="Documento de Google Sheets"
					>
						<MenuItem value={NO_WASHEET_DOCUMENT}>Seleccionar documento</MenuItem>
						{documents.map(document => (
							<MenuItem key={document.id} value={String(document.id)}>
								{document.name}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				<Typography className={`${classes.status} ${statusClass}`}>
					{loading && <CircularProgress size={12} className={classes.progress} />}
					{status.text}
				</Typography>

				{selectedDocument && (
					<>
						{records.length ? (
							records.map((record, index) => (
								<Paper
									key={`${record["ID Registro"] || index}`}
									variant="outlined"
									className={classes.record}
								>
									<div className={classes.recordHeader}>
										<Typography variant="subtitle2">Registro {index + 1}</Typography>
										<IconButton size="small" onClick={() => openRecordDialog(record)}>
											<EditIcon fontSize="small" />
										</IconButton>
									</div>
									{Object.entries(record)
										.filter(([, value]) => value !== null && value !== "")
										.map(([key, value]) => (
											<Typography
												key={key}
												variant="body2"
												className={classes.field}
											>
												<strong>{key}:</strong> {String(value)}
											</Typography>
										))}
								</Paper>
							))
						) : (
							<Typography className={classes.empty} variant="body2">
								No hay registros para este chat.
							</Typography>
						)}
						<Button
							variant="contained"
							color="primary"
							size="small"
							className={classes.addRecordButton}
							disabled={loading || !headers.length}
							onClick={() =>
								selectedDocument.allowMultipleRecords === false && records[0]
									? openRecordDialog(records[0])
									: openRecordDialog()
							}
						>
							{selectedDocument.allowMultipleRecords === false && records[0]
								? "Editar registro"
								: "Agregar registro"}
						</Button>
					</>
				)}

				<Typography variant="subtitle2">Configuracion de documentos</Typography>
				{documents.length ? (
					documents.map(document => (
						<Paper
							key={document.id}
							square
							variant="outlined"
							className={classes.documentRow}
						>
							<div className={classes.documentText}>
								<Typography variant="body2">{document.name}</Typography>
								<Typography variant="caption" className={classes.documentId}>
									{document.spreadsheetId}
								</Typography>
							</div>
							<div className={classes.documentActions}>
								<FormControlLabel
									control={
										<Switch
											size="small"
											color="primary"
											checked={document.allowMultipleRecords !== false}
											onChange={event =>
												handleToggleMultipleRecords(document, event.target.checked)
											}
										/>
									}
									label="Multiples registros"
								/>
								<IconButton
									size="small"
									onClick={() => setDeletingDocument(document)}
								>
									<DeleteOutlineIcon fontSize="small" />
								</IconButton>
							</div>
						</Paper>
					))
				) : (
					<Typography className={classes.empty} variant="body2">
						No hay documentos configurados.
					</Typography>
				)}

				<div className={classes.row}>
					<TextField
						label="ID del documento"
						value={docId}
						onChange={event => setDocId(event.target.value)}
						variant="outlined"
						margin="dense"
						size="small"
						fullWidth
					/>
					<TextField
						label="Hoja"
						value={sheetName}
						onChange={event => setSheetName(event.target.value)}
						variant="outlined"
						margin="dense"
						size="small"
						style={{ width: 92 }}
					/>
				</div>
				<Button variant="outlined" color="primary" size="small" onClick={handleAddDocument}>
					Agregar documento
				</Button>
				<FormControlLabel
					control={
						<Switch
							size="small"
							color="primary"
							checked={allowMultipleRecords}
							onChange={event => setAllowMultipleRecords(event.target.checked)}
						/>
					}
					label="Multiples registros"
				/>
				<Typography variant="caption" color="textSecondary">
					El ID esta en la URL de Google Sheets, entre /d/ y /edit.
				</Typography>
			</Collapse>

			<Dialog
				open={recordDialogOpen}
				onClose={() => setRecordDialogOpen(false)}
				fullWidth
				maxWidth="xs"
			>
				<DialogTitle>
					{editingRecord ? "Editar registro" : "Agregar registro"}
				</DialogTitle>
				<DialogContent>
					{Object.keys(formValues).map(field => (
						<TextField
							key={field}
							label={field}
							value={formValues[field] === undefined ? "" : String(formValues[field])}
							onChange={event => {
								const { value } = event.target;
								setFormValues(prev => ({
									...prev,
									[field]: value,
								}));
							}}
							variant="outlined"
							margin="dense"
							fullWidth
							multiline={
								field !== "ID Registro" &&
								field !== "Chat ID" &&
								field !== "Nombre" &&
								field !== "Numero" &&
								field !== "Correo"
							}
							rows={field === "Observaciones" ? 3 : 2}
							InputProps={{
								readOnly:
									field === "ID Registro" ||
									field === "Chat ID" ||
									field === "Nombre" ||
									field === "Numero" ||
									field === "Correo",
							}}
						/>
					))}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setRecordDialogOpen(false)}>Cancelar</Button>
					<Button color="primary" variant="contained" onClick={handleSaveRecord}>
						{editingRecord ? "Actualizar" : "Guardar"}
					</Button>
				</DialogActions>
			</Dialog>
			<ConfirmationModal
				title="Eliminar documento"
				open={!!deletingDocument}
				onClose={() => setDeletingDocument(null)}
				onConfirm={() => {
					if (deletingDocument) {
						handleDeleteDocument(deletingDocument.id);
					}
				}}
			>
				{deletingDocument
					? `¿Seguro que deseas eliminar el documento "${deletingDocument.name}"?`
					: "¿Seguro que deseas eliminar este documento?"}
			</ConfirmationModal>
		</Paper>
	);
};

export default WasheetsPanel;

